import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import moment from "moment";
import { FiEdit } from "react-icons/fi";
import ReportTable from "../../components/ReportTable";
import EditTransactionModal from "./EditTransactionModal";
import Notifier from "../../components/Notifier";
import remove_button from "../../resources/remove.png";
import Api from "../../utils/Api";

const ArchiveTransactionList = forwardRef(({ items, lookups, listWasChanged, isMobile }, ref) => {
    const [itemList, setItemList] = useState(items || []);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const notifierRef = useRef(null);

    const categoryLookup = lookups.categories.reduce((acc, category) => {
        acc[category.id] = category.description;
        return acc;
    }, {});

    const tagLookup = lookups.tags.reduce((acc, tag) => {
        acc[tag.id] = tag.description;
        return acc;
    }, {});

    useImperativeHandle(ref, () => ({
        orderByAmount: () => {
            setItemList(
                [...itemList]
                    .sort((a, b) => {
                        if (a.amount > b.amount) return 1;
                        if (a.amount < b.amount) return -1;
                        return 0;
                    })
                    .reverse()
            );
        },
        setItems: (items) => {
            setItemList(items);
        }
    }));

    const deleteTransaction = async (id, type) => {
        try {
            const response = await Api.deleteTransaction(id, type);
            if (response.status === 200) {
                setItemList((prev) => prev.filter((item) => item.id !== id));
                listWasChanged();
                notifierRef.current?.showSuccess("נמחק בהצלחה");
            } else {
                if (response.status === 404) {
                    notifierRef.current?.showError("טרנזקציה לא נמצאה - לא נמחקה");
                } else {
                    notifierRef.current?.showError("שגיאה במחיקה");
                }
            }
        } catch (error) {
            console.log(`Error deleting Item ${id} of type ${type}`, error);
            notifierRef.current?.showError("שגיאה במחיקה");
        }
    };

    const openEditModal = (id) => {
        const transaction = itemList.find((item) => item.id === id);
        if (transaction) {
            setEditingTransaction(transaction);
        }
    };

    const handleEditSave = async ({ id, type, categoryId, tags, remarks, date }) => {
        try {
            const { response } = await Api.editTransaction({ id, type, categoryId, tags, remarks, date: date || undefined });
            if (response.success) {
                setItemList((prev) =>
                    prev.map((item) =>
                        item.id === id
                            ? {
                                ...item,
                                categoryId,
                                tags,
                                remarks,
                                time: date ? `${date}T${moment(item.time).format("HH:mm:ss")}` : item.time
                            }
                            : item
                    )
                );
                notifierRef.current?.showSuccess("עודכן בהצלחה");
            } else {
                notifierRef.current?.showError("שגיאה בעדכון");
            }
        } catch (error) {
            console.log(`Error editing Item ${id}`, error);
            notifierRef.current?.showError("שגיאה בעדכון");
        }
    };

    if (!itemList?.length) {
        return <div />
    }
    return (
        <div
            style={{
                background: "white",
                boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
                borderRadius: "10px",
                margin: "auto",
                marginTop: "40px",
                direction: "rtl",
                width: "100%"
            }}
        >
            <ReportTable
                isMobile={isMobile}
                data={{
                    columns: [
                        { description: "זמן", field: "time", visibleOnMobile: true },
                        { description: "תיאור", type: "longText", field: "description", visibleOnMobile: true },
                        { description: "קטגוריה", field: "category", type: "longText", visibleOnMobile: true },
                        { description: "כמות", type: "number", field: "amount", visibleOnMobile: true },
                        { description: "תגיות", type: "longText", field: "tags" },
                        { description: "הערות", type: "longText", field: "remarks" },
                        { description: "כ.אשראי / חשבון", field: "creditCard" },
                        {
                            description: "ערוך",
                            type: "iconButton",
                            icon: FiEdit,
                            onClick: (id) => {
                                openEditModal(id);
                            },
                            visibleOnMobile: true
                        },
                        {
                            description: "מחק",
                            type: "button",
                            icon: remove_button,
                            onClick: (id, type) => {
                                deleteTransaction(id, type);
                            },
                            visibleOnMobile: false
                        }
                    ],
                    rows: itemList.map((item) => {
                        return {
                            id: item.id,
                            type: item.type,
                            time: moment(item.time).format("DD-MM-YY"),
                            description: item.description,
                            category: categoryLookup[item.categoryId],
                            amount: item.amount,
                            tags: (item.tags || []).map((id) => tagLookup[id]).filter(Boolean).join(", "),
                            remarks: item.remarks,
                            creditCard: item.creditCard,
                            accountNumber: item.accountNumber
                        };
                    })
                }}
            />

            <EditTransactionModal
                show={!!editingTransaction}
                onHide={() => setEditingTransaction(null)}
                transaction={editingTransaction}
                lookups={lookups}
                onSave={handleEditSave}
            />

            <Notifier ref={notifierRef} />
        </div>
    )
});

export default ArchiveTransactionList;